import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Col, Row } from 'react-bootstrap'
import Confetti from 'react-confetti'
import queryString from 'query-string'

import { track } from 'lib/analytics'
import { openRegItemEditModal } from 'registry/modals/actions'
import { getRegItem } from 'registry/reducers'
import { REGISTRY } from 'registry/constants'
import useWindowSize from 'lib/hooks/useWindowSize'
import { useDisplayBirthdayWishlistModal } from 'shared/BirthdayWishlistModal/BirthdayWishlistModal.utils'
import { useDisplayRegistryFeedbackModal } from 'shared/RegistryFeedbackModal/RegistryFeedbackModal.utils'
import { useDisplayRegistryOrganizationFeedbackModal } from 'shared/RegistryOrganizationFeedbackModal/RegistryOrganizationFeedbackModal.utils'
import { DismissableAppPromoBanner } from 'components/banners/app-promo-banner'
import RegistryOverviewContainer from './registry-overview'
import RegItemCardGhostList from './reg-item-card-ghost'
import {
  RegItemsHeaderContributor,
  RegItemsHeaderDiscount,
} from './reg-items-headers/reg-items-header'
import RegItemCategoryList from './reg-item-category-list/reg-item-category-list'
import { registryPageDataFetcher } from './registry-page-data-fetcher'
import NoRegItemsMessage from './NoRegItemsMessage'
import RegistrySideNavContainer from './registry-sidenav'
import Organize from './organize'
import useFeatureFlag from 'shared/hooks/useFeatureFlag/useFeatureFlag'

const RegistryPageContributor = ({
  addFilter,
  categorizedRegItems,
  editRegItemId,
  filters,
  filteredRegItemsCount,
  hasRegistryDiscountEligibleItems,
  isAllCategoriesCollapsed,
  isMobile,
  isOrganizing,
  isOwner,
  isPageReady,
  isRegistryDiscountView,
  location,
  openMobileFiltersModal,
  openRegItemEditModal,
  populatedCategories,
  regItemsCount,
  registry,
  reservationsByCurrentVisitor,
  setFilters,
  setIsOrganizing,
  setOrganizing,
  showAppPromoBanner,
  stores,
}) => {
  const { width, height } = useWindowSize()
  const params = queryString.parse(location.search)
  const [showConfetti, setShowConfetti] = useState(params.confetti)
  const { flagValue: registryRevamp } = useFeatureFlag(
    '24b_ff_registry_registry_revamp',
    false
  )

  if (params.show_registry_revamp !== 'false' && registryRevamp) {
    window.location.reload()
    return null
  }

  useDisplayBirthdayWishlistModal(
    registry,
    track.EventLocation.LIST_ADOPTION_MODAL_MINUS_REGISTRY
  )

  useDisplayRegistryFeedbackModal(
    registry,
    track.EventLocation.LIST_ADOPTION_MODAL_MINUS_REGISTRY
  )

  useDisplayRegistryOrganizationFeedbackModal(
    registry,
    track.EventLocation.LIST_ADOPTION_MODAL_MINUS_REGISTRY
  )

  // Supports opening the reg item edit modal via ?edit_reg_item=[id] query param
  const maybeOpenRegItemEditModal = () => {
    if (editRegItemId) {
      openRegItemEditModal(editRegItemId)
    }
  }

  useEffect(() => {
    setIsOrganizing(setOrganizing)
    maybeOpenRegItemEditModal()
  }, [setOrganizing, isPageReady])

  const { urlSlug } = registry

  return (
    <>
      {showConfetti && (
        <Confetti
          height={height}
          numberOfPieces={800}
          recycle={false}
          width={width}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}

      {showAppPromoBanner && <DismissableAppPromoBanner />}
      <div className="container mts mtl-sm pbxl" id="registry">
        <Row>
          <Col className="hidden-xs" md={3} sm={3} xl={2}>
            <RegistrySideNavContainer
              activeNav={REGISTRY}
              addFilter={!isOrganizing ? addFilter : null}
              categories={populatedCategories}
              isRegistryDiscountView={isRegistryDiscountView}
              registryUrlSlug={urlSlug}
              stores={stores}
            />
          </Col>

          <Col md={9} sm={9} xl={10}>
            {!isRegistryDiscountView && <RegistryOverviewContainer />}

            {isRegistryDiscountView ? (
              <RegItemsHeaderDiscount
                hasRegistryDiscountEligibleItems={
                  hasRegistryDiscountEligibleItems
                }
                isOwner={isOwner}
                openMobileFiltersModal={openMobileFiltersModal}
                ownerName={registry.ownerName}
                registryUrlSlug={registry.urlSlug}
              />
            ) : (
              <RegItemsHeaderContributor
                addFilter={addFilter}
                categories={populatedCategories}
                filters={filters}
                isOrganizing={isOrganizing}
                openMobileFiltersModal={openMobileFiltersModal}
                regItemsCount={regItemsCount}
                registryUrlSlug={urlSlug}
                stores={stores}
              />
            )}

            <div className="reg-items" data-heap="scroll-depth">
              {isPageReady && (
                <NoRegItemsMessage
                  isContributorView
                  clearFilters={setFilters}
                  filteredRegItemsCount={filteredRegItemsCount}
                  registryId={registry.id}
                  registryType={registry.type}
                  totalRegItemsCount={regItemsCount}
                />
              )}

              {isOrganizing ? (
                <Organize registryUrlSlug={urlSlug} />
              ) : (
                <RegItemCategoryList
                  isContributorView
                  categories={categorizedRegItems}
                  filters={filters}
                  isAllCategoriesCollapsed={isAllCategoriesCollapsed}
                  isMobile={isMobile}
                  isRegistryDiscountView={isRegistryDiscountView}
                  reservationsByCurrentVisitor={reservationsByCurrentVisitor}
                />
              )}

              {!isPageReady && categorizedRegItems.length < 1 ? (
                <RegItemCardGhostList />
              ) : null}
            </div>
          </Col>
        </Row>
      </div>
    </>
  )
}

RegistryPageContributor.propTypes = {
  addFilter: PropTypes.func.isRequired,
  categorizedRegItems: PropTypes.array.isRequired,
  editRegItemId: PropTypes.number,
  filters: PropTypes.object,
  filteredRegItemsCount: PropTypes.number.isRequired,
  hasRegistryDiscountEligibleItems: PropTypes.bool.isRequired,
  isAllCategoriesCollapsed: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isOrganizing: PropTypes.bool,
  isOwner: PropTypes.bool.isRequired,
  isPageReady: PropTypes.bool,
  isRegistryDiscountView: PropTypes.bool,
  openMobileFiltersModal: PropTypes.func.isRequired,
  openRegItemEditModal: PropTypes.func.isRequired,
  populatedCategories: PropTypes.array.isRequired,
  regItemsCount: PropTypes.number.isRequired,
  registry: PropTypes.object.isRequired,
  reservationsByCurrentVisitor: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  setIsOrganizing: PropTypes.func.isRequired,
  setOrganizing: PropTypes.bool.isRequired,
  showAppPromoBanner: PropTypes.bool.isRequired,
  stores: PropTypes.array.isRequired,
}

RegistryPageContributor.defaultProps = {
  editRegItemId: undefined,
  filters: {},
  isOrganizing: false,
  isPageReady: false,
  isRegistryDiscountView: false,
}

const mapStateToProps = (state, ownProps) => {
  let { edit_reg_item: editRegItemId } = queryString.parse(
    ownProps.location.search
  )
  // force edit reg item id to null if we actually don't have the reg item in redux state
  if (editRegItemId && !getRegItem(state, editRegItemId)) {
    editRegItemId = null
  }
  return {
    editRegItemId: parseInt(editRegItemId),
  }
}

export default connect(mapStateToProps, { openRegItemEditModal })(
  registryPageDataFetcher(RegistryPageContributor)
)
